import { api } from './api'
import type { VisitoriaData, VisitoriaFormData, VisitoriaListResponse } from '@/types'

export const visitoriaService = {
  // Listar vistorias
  async getAll(page?: number, limit?: number, withFiles?: boolean, referencia?: string): Promise<VisitoriaListResponse> {
    try {
      const params = new URLSearchParams()
      if (page) params.append('page', page.toString())
      if (limit) params.append('limit', limit.toString())
      if (withFiles) params.append('with_files', 'true')
      if (referencia) params.append('referencia', referencia)

      const response = await api.get(`/vistorias?${params}`)
      return response.data
    } catch (error: any) {
      console.error('Erro ao buscar vistorias:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao carregar vistorias',
        data: []
      }
    }
  },

  // Buscar vistoria por ID
  async getById(id: number, withFile?: boolean): Promise<{ success: boolean; data: VisitoriaData; message: string }> {
    try {
      const params = new URLSearchParams()
      if (withFile) params.append('with_file', 'true')

      const response = await api.get(`/vistorias/${id}?${params}`)
      return response.data
    } catch (error: any) {
      console.error('Erro ao buscar vistoria:', error)
      return {
        success: false,
        data: {} as VisitoriaData,
        message: error.response?.data?.message || 'Erro ao carregar vistoria'
      }
    }
  },

  // Criar nova vistoria
  async create(data: VisitoriaFormData, file?: File): Promise<{ success: boolean; data: VisitoriaData; message: string }> {
    try {
      console.log('📝 Dados recebidos para criação:', data)
      console.log('📁 Arquivo:', file ? file.name : 'Nenhum arquivo')
      
      if (file) {
        // Se há arquivo, usar FormData
        const formData = new FormData()
        formData.append('referencia', data.referencia)
        if (data.descricao) {
          formData.append('descricao', data.descricao)
        }
        formData.append('latitude', data.latitude.toString())
        formData.append('longitude', data.longitude.toString())
        if (data.accuracy) {
          formData.append('accuracy', data.accuracy.toString())
        }
        formData.append('file', file)

        console.log('📤 Enviando FormData com campos:', {
          referencia: data.referencia,
          descricao: data.descricao,
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: data.accuracy,
          file: file.name
        })

        const response = await api.post('/vistorias', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return response.data
      } else {
        // Sem arquivo, usar JSON normal
        console.log('📤 Enviando JSON com campos:', data)
        const response = await api.post('/vistorias', data)
        return response.data
      }
    } catch (error: any) {
      console.error('Erro ao criar vistoria:', error)
      return {
        success: false,
        data: {} as VisitoriaData,
        message: error.response?.data?.message || 'Erro ao criar vistoria'
      }
    }
  },

  // Atualizar vistoria
  async update(id: number, data: Partial<VisitoriaFormData>): Promise<{ success: boolean; data: VisitoriaData; message: string }> {
    try {
      console.log('📝 Dados recebidos para atualização:', data)
      console.log('🆔 ID da vistoria:', id)
      const response = await api.put(`/vistorias/${id}`, data)
      return response.data
    } catch (error: any) {
      console.error('Erro ao atualizar vistoria:', error)
      return {
        success: false,
        data: {} as VisitoriaData,
        message: error.response?.data?.message || 'Erro ao atualizar vistoria'
      }
    }
  },

  // Deletar vistoria
  async delete(id: number): Promise<{ success: boolean; message: string }> {
    try {
      if (!id || isNaN(Number(id))) {
        return {
          success: false,
          message: 'ID da vistoria é inválido'
        }
      }

      const response = await api.delete(`/vistorias/${id}`)
      return response.data
    } catch (error: any) {
      console.error('Erro ao deletar vistoria:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao deletar vistoria'
      }
    }
  },

  // Buscar por referência
  async search(referencia: string): Promise<{ success: boolean; data: VisitoriaData[]; message: string }> {
    try {
      const response = await api.get(`/vistorias/search?referencia=${encodeURIComponent(referencia)}`)
      return response.data
    } catch (error: any) {
      console.error('Erro ao buscar vistorias:', error)
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Erro ao buscar vistorias'
      }
    }
  },

  // Contar vistorias
  async count(): Promise<{ success: boolean; data: { total: number }; message: string }> {
    try {
      const response = await api.get('/vistorias/count')
      return response.data
    } catch (error: any) {
      console.error('Erro ao contar vistorias:', error)
      return {
        success: false,
        data: { total: 0 },
        message: error.response?.data?.message || 'Erro ao contar vistorias'
      }
    }
  },

  // Vistorias com arquivos
  async getWithFiles(page?: number, limit?: number): Promise<VisitoriaListResponse> {
    try {
      const params = new URLSearchParams()
      if (page) params.append('page', page.toString())
      if (limit) params.append('limit', limit.toString())

      const response = await api.get(`/vistorias/with-files?${params}`)
      return response.data
    } catch (error: any) {
      console.error('Erro ao buscar vistorias com arquivos:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao carregar vistorias',
        data: []
      }
    }
  },

  // Vistorias sem arquivos
  async getWithoutFiles(): Promise<{ success: boolean; data: VisitoriaData[]; message: string }> {
    try {
      const response = await api.get('/vistorias/without-files')
      return response.data
    } catch (error: any) {
      console.error('Erro ao buscar vistorias sem arquivos:', error)
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Erro ao carregar vistorias'
      }
    }
  }
}
